import { useRef } from "react";
import { motion, useScroll, useTransform, useMotionValue, useSpring } from "framer-motion";
import { ArrowDownRight, MessageCircle } from "lucide-react";
import { waLink } from "./data";
import { scrollToId } from "./scroll";

const EASE = [0.16, 1, 0.3, 1];

const Line = ({ children, i }) => (
  <span className="block overflow-hidden pb-[0.08em]">
    <motion.span className="block" initial={{ y: "110%" }} animate={{ y: "0%" }}
      transition={{ duration: 1.2, delay: 0.25 + i * 0.13, ease: EASE }}>
      {children}
    </motion.span>
  </span>
);

const Badge = () => (
  <div className="pointer-events-none absolute bottom-10 right-6 hidden h-36 w-36 md:block lg:right-16">
    <svg viewBox="0 0 200 200" className="spin-slow h-full w-full">
      <defs><path id="c" d="M100,100 m-78,0 a78,78 0 1,1 156,0 a78,78 0 1,1 -156,0" /></defs>
      <text fill="#F6F1E4" fontSize="17" fontWeight="700" letterSpacing="5.5" fontFamily="Manrope">
        <textPath href="#c">VIAJES A BRASIL · A TU MEDIDA · 2026/27 ·</textPath>
      </text>
    </svg>
    <div className="absolute inset-0 m-auto flex h-14 w-14 items-center justify-center rounded-full bg-sol">
      <svg viewBox="0 0 24 24" className="h-7 w-7"><path d="M12 2 22 12 12 22 2 12Z" fill="#06301F" /></svg>
    </div>
  </div>
);

export const Hero = () => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const imgY = useTransform(scrollYProgress, [0, 1], ["0%", "22%"]);
  const imgScale = useTransform(scrollYProgress, [0, 1], [1.08, 1.22]);
  const textY = useTransform(scrollYProgress, [0, 1], ["0%", "35%"]);
  const mx = useMotionValue(0);
  const my = useMotionValue(0);
  const rx = useSpring(useTransform(my, [-0.5, 0.5], [3, -3]), { stiffness: 60, damping: 20 });
  const ry = useSpring(useTransform(mx, [-0.5, 0.5], [-4, 4]), { stiffness: 60, damping: 20 });

  const onMove = (e) => {
    const r = e.currentTarget.getBoundingClientRect();
    mx.set((e.clientX - r.left) / r.width - 0.5);
    my.set((e.clientY - r.top) / r.height - 0.5);
  };

  return (
    <section id="top" ref={ref} onMouseMove={onMove} data-testid="hero-section"
      className="relative min-h-[100svh] overflow-hidden bg-selva [perspective:1400px]">
      <motion.div style={{ y: imgY, scale: imgScale, rotateX: rx, rotateY: ry }} className="absolute inset-0"
        initial={{ clipPath: "inset(12% 10% 12% 10% round 40px)" }} animate={{ clipPath: "inset(0% 0% 0% 0% round 0px)" }}
        transition={{ duration: 1.6, ease: EASE }}>
        <img src="/img/hero.jpg" alt="Bahía de Guanabara y Pão de Açúcar, Rio de Janeiro" className="h-full w-full object-cover" />
      </motion.div>
      <div className="absolute inset-0 bg-gradient-to-t from-selva via-selva/55 to-selva/25" />
      <div className="grain absolute inset-0" />

      <motion.div style={{ y: textY }} className="relative z-10 mx-auto flex min-h-[100svh] max-w-7xl flex-col justify-end px-5 pb-16 pt-32 sm:px-8 lg:px-16 lg:pb-24">
        <motion.p initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 1.1, duration: 0.8 }}
          className="mb-6 inline-flex w-fit items-center gap-2 rounded-full border border-arena/30 bg-selva/40 px-4 py-2 text-xs font-bold uppercase tracking-[0.22em] text-arena backdrop-blur-md"
          data-testid="hero-badge">
          <span className="h-2 w-2 animate-pulse rounded-full bg-brasil ring-2 ring-sol" /> Planificación 2026 / 2027 abierta
        </motion.p>
        <h1 data-testid="hero-title" className="font-display text-[15vw] font-extrabold leading-[0.88] tracking-[-0.04em] text-arena sm:text-[11vw] lg:text-[8.6rem]">
          <Line i={0}>Brasil,</Line>
          <Line i={1}><span className="font-serif-i font-normal tracking-[-0.02em] text-sol">diseñado</span></Line>
          <Line i={2}>a tu medida.</Line>
        </h1>
        <div className="mt-10 flex flex-col gap-8 md:flex-row md:items-end md:justify-between">
          <motion.p initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 1, duration: 0.9, ease: EASE }}
            className="max-w-md text-base leading-relaxed text-arena/90 md:text-lg">
            Planificamos tu viaje a Brasil de principio a fin: playas, selva, cataratas y ciudades, con asistencia real por WhatsApp.
          </motion.p>
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 1.15, duration: 0.9, ease: EASE }}
            className="flex flex-col gap-3 sm:flex-row md:mr-44">
            <button data-testid="hero-cta-plan" onClick={() => scrollToId("contacto")}
              className="group flex items-center justify-center gap-3 rounded-full bg-barro px-7 py-4 font-bold text-white shadow-[0_15px_35px_-12px_rgba(213,43,30,0.8)] transition-transform duration-300 hover:-translate-y-1">
              Diseñar mi viaje <ArrowDownRight className="h-5 w-5 transition-transform duration-300 group-hover:rotate-[-45deg]" />
            </button>
            <a data-testid="hero-cta-whatsapp" href={waLink()} target="_blank" rel="noopener noreferrer"
              className="flex items-center justify-center gap-2 rounded-full border border-arena/40 px-7 py-4 font-bold text-arena backdrop-blur-sm transition-colors duration-300 hover:bg-arena hover:text-selva">
              <MessageCircle className="h-5 w-5" /> WhatsApp
            </a>
          </motion.div>
        </div>
      </motion.div>
      <Badge />
    </section>
  );
};
